import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  inject,
  signal,
} from '@angular/core';
import { EventType, FlashMapComponent, FlashMapService } from 'flash-map';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { MindmapTranslations } from '../../mindmap-translations';

type WritableSignal<T> = {
  (): T;
  set: (value: T) => void;
};

type ZoomPanelApi = {
  mode: WritableSignal<'map' | 'outline'>;
  outlineEnabled: () => boolean;
  showZoomControl?: () => boolean;
};

export type MapViewMode = 'mindmap' | 'outline' | 'focus';

const DETAIL_COUNT_MIN = 1;
const DETAIL_COUNT_MAX = 5;

@Component({
  selector: 'app-map-zoom-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './map-zoom-panel.html',
  styleUrl: './map-zoom-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class MapZoomPanelComponent implements OnChanges, OnDestroy {
  private readonly uiTranslation = inject(UiTranslationService);
  protected readonly i18n =
    this.uiTranslation.namespace<MindmapTranslations>('mindmap');
  @Input() flashMap: FlashMapComponent | null = null;
  @Output() modeChange = new EventEmitter<MapViewMode>();
  private zoomEventBound = false;

  readonly zoomLevel = signal(100);
  readonly zoomDropdownOpen = signal(false);
  readonly modeDropdownOpen = signal(false);
  readonly detailPanelOpen = signal(false);
  readonly detailCount = signal(3);
  readonly selectedMode = signal<MapViewMode>('mindmap');
  readonly panelExpanded = signal(false);
  readonly hoverExpanded = signal(false);
  readonly supportsHover =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(hover: hover) and (pointer: fine)').matches === true;
  private hoverCollapseTimer: ReturnType<typeof setTimeout> | null = null;

  readonly zoomOptions = [
    { label: '200%', value: 200 },
    { label: '150%', value: 150 },
    { label: '100%', value: 100 },
    { label: '75%', value: 75 },
    { label: '50%', value: 50 },
  ];

  get panel(): ZoomPanelApi | null {
    if (!this.flashMap) {
      return null;
    }
    return this.flashMap as unknown as ZoomPanelApi;
  }

  ngOnChanges(): void {
    this.bindZoomEvent();
    this.syncZoomLevel();
    this.syncSelectedModeFromPanel();
  }

  ngOnDestroy(): void {
    this.clearHoverCollapseTimer();
  }

  isPanelExpanded(): boolean {
    return this.panelExpanded() || this.hoverExpanded();
  }

  onPanelMouseEnter(): void {
    this.clearHoverCollapseTimer();
    this.hoverExpanded.set(true);
  }

  onPanelMouseLeave(): void {
    this.scheduleHoverCollapse();
  }

  isVisible(panel: ZoomPanelApi): boolean {
    return panel.showZoomControl ? panel.showZoomControl() : true;
  }

  toggleModeDropdown(): void {
    this.clearNodeSelection();
    this.detailPanelOpen.set(false);
    this.zoomDropdownOpen.set(false);
    if (!this.supportsHover && !this.panelExpanded()) {
      this.panelExpanded.set(true);
      this.modeDropdownOpen.set(true);
      return;
    }
    this.modeDropdownOpen.set(!this.modeDropdownOpen());
  }

  onSelectMode(panel: ZoomPanelApi, mode: MapViewMode): void {
    this.clearNodeSelection();
    const nextMode = mode === 'outline' ? 'outline' : 'map';
    this.expandPanelForTouch(false);
    this.modeDropdownOpen.set(false);
    this.zoomDropdownOpen.set(false);
    if (mode !== 'mindmap') {
      this.detailPanelOpen.set(false);
    }
    panel.mode.set(nextMode);
    this.selectedMode.set(mode);
    this.modeChange.emit(mode);
  }

  currentModeIcon(): string {
    switch (this.selectedMode()) {
      case 'outline':
        return 'list';
      case 'focus':
        return 'target';
      default:
        return 'git-branch';
    }
  }

  onZoomIn(): void {
    const service = this.getService();
    if (!service) {
      return;
    }
    service.selectClear();
    service.zoomIn();
    this.syncZoomLevel();
  }

  onZoomOut(): void {
    const service = this.getService();
    if (!service) {
      return;
    }
    service.selectClear();
    service.zoomOut();
    this.syncZoomLevel();
  }

  onResetZoom(): void {
    const service = this.getService();
    if (!service) {
      return;
    }
    service.selectClear();
    service.resetZoom();
    this.zoomLevel.set(100);
  }

  onCenter(): void {
    const service = this.getService();
    if (!service) {
      return;
    }
    service.selectClear();
    service.centerNode();
  }

  toggleDropdown(): void {
    this.clearNodeSelection();
    this.detailPanelOpen.set(false);
    this.modeDropdownOpen.set(false);
    if (!this.supportsHover && !this.panelExpanded()) {
      this.panelExpanded.set(true);
      this.zoomDropdownOpen.set(true);
      return;
    }
    this.zoomDropdownOpen.set(!this.zoomDropdownOpen());
  }

  toggleDetailPanel(): void {
    this.clearNodeSelection();
    this.zoomDropdownOpen.set(false);
    this.modeDropdownOpen.set(false);
    this.expandPanelForTouch(false);
    this.detailPanelOpen.set(!this.detailPanelOpen());
  }

  onDetailCountInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.setDetailCount(input?.valueAsNumber ?? Number(input?.value));
  }

  detailMax(): number {
    return DETAIL_COUNT_MAX;
  }

  detailMin(): number {
    return DETAIL_COUNT_MIN;
  }

  isDetailControlAvailable(): boolean {
    const service = this.getService();
    return service?.isReady() === true && service.getRoot() !== null;
  }

  setZoomLevel(level: number): void {
    const service = this.getService();
    if (!service) {
      return;
    }
    service.selectClear();
    service.setZoom(level / 100);
    this.zoomLevel.set(level);
    this.zoomDropdownOpen.set(false);
  }

  fitMap(): void {
    const service = this.getService();
    const container = service?.getContainer() ?? null;
    if (!service || !container) {
      this.zoomDropdownOpen.set(false);
      return;
    }
    service.selectClear();
    service.setZoom(1);
    requestAnimationFrame(() => {
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const canvas = container.querySelector('jmnodes');
      if (!canvas) {
        this.zoomDropdownOpen.set(false);
        return;
      }
      const nodes = canvas.querySelectorAll('jmnode');
      if (!nodes.length) {
        this.zoomDropdownOpen.set(false);
        return;
      }
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      nodes.forEach((node) => {
        const element = node as HTMLElement;
        const left = element.offsetLeft;
        const top = element.offsetTop;
        const right = left + element.offsetWidth;
        const bottom = top + element.offsetHeight;
        minX = Math.min(minX, left);
        minY = Math.min(minY, top);
        maxX = Math.max(maxX, right);
        maxY = Math.max(maxY, bottom);
      });
      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;
      const padding = 0.85;
      const zoomX = (containerWidth * padding) / contentWidth;
      const zoomY = (containerHeight * padding) / contentHeight;
      let optimalZoom = Math.min(zoomX, zoomY);
      optimalZoom = Math.max(0.1, Math.min(2, optimalZoom));
      const zoomPercentage = Math.round(optimalZoom * 100);
      service.setZoom(optimalZoom);
      this.zoomLevel.set(zoomPercentage);
      requestAnimationFrame(() => {
        service.centerNode();
      });
    });
    this.zoomDropdownOpen.set(false);
  }

  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const zoomControl = target?.closest?.('.zoom-control-panel') ?? null;
    if (!zoomControl && this.zoomDropdownOpen()) {
      this.zoomDropdownOpen.set(false);
    }
    if (!zoomControl && this.modeDropdownOpen()) {
      this.modeDropdownOpen.set(false);
    }
    if (!zoomControl && this.detailPanelOpen()) {
      this.detailPanelOpen.set(false);
    }
    if (!zoomControl && !this.supportsHover && this.panelExpanded()) {
      this.panelExpanded.set(false);
    }
  }

  private setDetailCount(value: number): void {
    this.clearNodeSelection();
    const max = this.detailMax();
    const nextValue = this.clampDetailCount(value, max);
    this.detailCount.set(nextValue);
    this.applyDetailCount(nextValue);
  }

  private applyDetailCount(count: number): void {
    const service = this.getService();
    if (!service || !service.isReady() || service.getRoot() === null) {
      return;
    }

    service.expandToDepth(count);
  }

  private clampDetailCount(value: number, max: number): number {
    if (!Number.isFinite(value)) {
      return DETAIL_COUNT_MIN;
    }
    return Math.min(Math.max(Math.round(value), DETAIL_COUNT_MIN), max);
  }

  private scheduleHoverCollapse(): void {
    this.clearHoverCollapseTimer();
    this.hoverCollapseTimer = setTimeout(() => {
      this.hoverExpanded.set(false);
      this.hoverCollapseTimer = null;
    }, 320);
  }

  private clearHoverCollapseTimer(): void {
    if (this.hoverCollapseTimer !== null) {
      clearTimeout(this.hoverCollapseTimer);
      this.hoverCollapseTimer = null;
    }
  }

  private expandPanelForTouch(openDropdown: boolean): void {
    if (this.supportsHover) {
      return;
    }
    this.panelExpanded.set(true);
    if (openDropdown) {
      this.zoomDropdownOpen.set(true);
    }
  }

  isModeActive(mode: MapViewMode): boolean {
    return this.selectedMode() === mode;
  }

  private syncZoomLevel(): void {
    const service = this.getService();
    if (!service || !service.isReady()) {
      return;
    }
    const currentZoom = service.getZoom();
    this.zoomLevel.set(Math.round(currentZoom * 100));
  }

  private bindZoomEvent(): void {
    if (this.zoomEventBound) {
      return;
    }
    const service = this.getService();
    if (!service) {
      return;
    }
    service.addEventListener((type) => {
      if (type !== EventType.RESIZE) {
        return;
      }
      this.syncZoomLevel();
    });
    this.zoomEventBound = true;
  }

  private syncSelectedModeFromPanel(): void {
    const panel = this.panel;
    if (!panel || this.selectedMode() === 'focus') {
      return;
    }

    this.selectedMode.set(panel.mode() === 'outline' ? 'outline' : 'mindmap');
  }

  private getService(): FlashMapService | null {
    return (
      (this.flashMap as unknown as { flashMapService?: FlashMapService })
        ?.flashMapService ?? null
    );
  }

  private clearNodeSelection(): void {
    this.getService()?.selectClear();
  }
}
